import QRCode from 'qrcode';
import { matchPath } from 'react-router';

import { takeEvery, call, select, actionChannel, take, spawn, cancel, fork, race } from 'redux-saga/effects';

import * as types from 'modules/app/actionTypes';
import { SIGN_OUT_SUCCESS } from 'modules/auth/actionTypes';
import { FETCH_GROUP_FAILURE, FETCH_GROUP_SUCCESS } from 'modules/groups/actionTypes';

import { getSelectedGroupId } from 'modules/groups/selectors';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { getLocation, getPreviousLocation } from 'modules/router/selectors';
import { downloadURL } from 'services/Utils';

import { applicationIsReady } from '../selectors';

/**
 * @param {string} pathname
 * @param {import('constants/RoutesConstants').FBPath[]} paths
 * @returns {boolean}
 */
function matchGivenLocation(pathname, paths) {
  return Boolean(pathname) && paths.some(
    path => matchPath(path.path, pathname) !== null || (path.simplePath && matchPath(path.simplePath, pathname) !== null)
  );
}

/**
 * @param {import('constants/RoutesConstants').FBPath[]} paths
 * @returns {boolean}
 */
export function* matchLocation(paths) {
  const { pathname } = yield select(getLocation);
  return matchGivenLocation(pathname, paths);
}

/**
 * @param {import('constants/RoutesConstants').FBPath[]} paths
 * @returns {boolean}
 */
function* matchPreviousLocation(paths) {
  const { pathname } = yield select(getPreviousLocation);
  return matchGivenLocation(pathname, paths);
}

/** @param {import('../types').GenerateQRCodeAction} action */
function* generateQRCode({ payload }) {
  const { url, filename = 'qr_code.png', options = { width: 500 } } = payload;
  const qrCode = yield call([QRCode, QRCode.toDataURL], url, options);
  yield call(downloadURL, qrCode, filename);
}

/**
 * @param {import('constants/RoutesConstants').FBPath[]} paths
 * @param {(...args: any[]) => Generator} onScope
 * @param {(...args: any[]) => Generator} [onLeave]
 * @param {ActionPattern} [channelPattern]
 */
export function* scopeToPage(paths, onScope, onLeave, channelPattern = LOCATION_CHANGE) {
  return yield spawn(function* root() {
    let childProcess;
    const channel = yield actionChannel(channelPattern);
    const isReady = yield select(applicationIsReady);
    if (!isReady) {
      yield take(types.APPLICATION_IS_READY);
    }

    do {
      const racer = yield race({
        chan: take(channel),
        logout: take(SIGN_OUT_SUCCESS)
      });

      if (childProcess) {
        yield cancel(childProcess);
      }

      const isOnPage = yield call(matchLocation, paths);
      if (racer.chan && isOnPage) {
        if (typeof onScope === 'function') {
          childProcess = yield fork(onScope);
        }
      } else if (typeof onLeave === 'function') {
        const wasOnPage = yield call(matchPreviousLocation, paths);
        if (wasOnPage) {
          childProcess = yield fork(onLeave);
        }
      }
    } while (true);
  });
}

/**
 * @param {boolean} [waitForGroup=false] If true, waits for the group to be loaded.
 */
export function* waitForApplicationReady(waitForGroup = false) {
  const isDealerReady = yield select(applicationIsReady);
  const isGroupReady = yield select(getSelectedGroupId);

  if (!isDealerReady) {
    yield take(types.APPLICATION_IS_READY);
  }
  if (waitForGroup && !isGroupReady) {
    yield take([FETCH_GROUP_SUCCESS, FETCH_GROUP_FAILURE]);
  }
}

export default function* utilsSagas() {
  yield takeEvery(types.GENERATE_QR_CODE, generateQRCode);
}
